import { type ReactNode, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Card } from '@/ui-kit/molecules/card';
import { Badge } from '@/ui-kit/atoms/badge';
import { Button } from '@/ui-kit/atoms/button';
import { LoadingSpinner } from '@/ui-kit/atoms/loading-spinner';
import { EmptyState } from '@/ui-kit/atoms/empty-state';
import { FormatDate } from '@/ui-kit/atoms/format-date';
import { FormatMoney } from '@/ui-kit/atoms/format-money';
import {
  type CustomerSource,
  DEFAULT_SOURCE,
  customerDetailPath,
  customerOrdersPath,
  orderDetailPath,
} from '@/app-kit/customer-source';
import { useCurrentUserContext } from '@/app-kit/current-user-context';
import { useCustomer } from '@/app-kit/use-customers';
import { useSortedRows } from '@/app-kit/use-table-sort';
import {
  useOrder,
  useOrderCommissions,
  useOrderEdit,
  useOrderLinesEdit,
  useOrderLookups,
  useOrderNeighbours,
  useOrderTimeline,
  ORDER_NEIGHBOURS_KEY,
  type ClosedPeriod,
  type OrderCommissionRow,
  type OrderHeader,
  type OrderLine,
  type OrderLookups,
  type OrderTimelineEvent,
} from '@/app-kit/use-orders';
import {
  OrderPayoutsTable,
  SortableTh,
  Td,
  Muted,
  decimal2,
  bonusTypeRank,
} from './order-payouts-table';
import { EditFieldGroupModal, type FieldGroupConfig } from './edit-field-group-modal';
import { applyAccess, type AccessMap } from './access-map';
import { EditOrderLinesModal } from './edit-order-lines-modal';
import { editableLineFields } from './order-lines-edit';
import { Section, Field, SectionAccess } from './section';
import { ShopifyOrderLink } from './shopify-customer-link';
import { orderFieldLabel } from './order-field-labels';
import { orderFiltersFromSearch, orderSortFromSearch } from './order-list-search';

export type OrderDetailPageProps = { source?: CustomerSource };

/**
 * Exigo order custom-field slots shown in Block 3: the left column's slots sit
 * under Commissioned Date, the right column holds the rest. `other13` is
 * included so the slot is visible, but it has no name in Exigo —
 * `orderFieldLabel` falls back to the raw slot name until one is supplied.
 */
const ORDER_SLOTS_LEFT = ['other11', 'other15', 'other12'] as const;
const ORDER_SLOTS_RIGHT = [
  'other14', 'other13', 'other16', 'other17', 'other18', 'other19', 'other20',
] as const;

/**
 * Order detail tabs are real URL segments, matching the customer detail page
 * (`customer-layout.tsx`): `NavLink` + `Outlet`, with the default tab at the
 * index route. Component state was the original implementation and meant tab
 * switches never touched the URL — Back skipped the whole page and a tab could
 * not be linked to or refreshed into.
 */
// Tab ids are QUALIFIED — `timeline` is a tab on customers.detail too, and
// resolving a bare id flatly is the collision shape the qualification rule
// exists to prevent.
const TABS: Array<{ to: string; label: string; end?: boolean; reachTab: string }> = [
  { to: '.',            label: 'Invoice',     end: true, reachTab: 'orders.detail.invoice' },
  { to: 'commissions',  label: 'Commissions',            reachTab: 'orders.detail.commissions' },
  { to: 'timeline',     label: 'Change Log',             reachTab: 'orders.detail.timeline' },
];

/** Shape passed to the order tabs via `<Outlet context>`. */
export type OrderOutletCtx = {
  order:     OrderHeader;
  lineItems: OrderLine[];
  source:    CustomerSource;
};

export function useOrderOutletCtx(): OrderOutletCtx {
  return useOutletContext<OrderOutletCtx>();
}

/**
 * The fields the edit modal writes, by name.
 *
 * Hoisted out of `editConfig` because the Edit BUTTON must decide before the
 * modal's config exists — and because `_access` is keyed by field name, so the
 * affordance needs the names and nothing else.
 *
 * ⚠️ Kept in sync with `editConfig.fields` below by hand. A name added there
 * and missed here only makes the button slightly too conservative; the reverse
 * would offer an edit the PATCH refuses, which the server still catches.
 */
const EDITABLE_ORDER_FIELDS = [
  'order_date', 'order_status_id', 'notes', 'customer_id', 'is_commissionable',
] as const;

export function OrderDetailPage({ source = DEFAULT_SOURCE }: OrderDetailPageProps = {}) {
  const { id = '', orderId = '' } = useParams<{ id: string; orderId: string }>();
  const { search } = useLocation();
  const orderQuery = useOrder(orderId, { source });

  if (orderQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (orderQuery.error || !orderQuery.data) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-gray-900">Order not found</h1>
        {orderQuery.error?.message && (
          <p className="text-sm text-gray-500">{orderQuery.error.message}</p>
        )}
        <Link to={`${customerOrdersPath(source, id)}${search}`} className="text-brand-600 hover:underline">
          ← Back to orders
        </Link>
      </div>
    );
  }

  const { order, line_items, line_access, closed_period } = orderQuery.data;
  return (
    <OrderDetailBody
      order={order}
      lineItems={line_items}
      lineAccess={line_access}
      closedPeriod={closed_period ?? null}
      source={source}
      customerUrlId={id}
    />
  );
}

function OrderDetailBody({
  order, lineItems, lineAccess, closedPeriod, source, customerUrlId,
}: {
  order:         OrderHeader;
  lineItems:     OrderLine[];
  lineAccess:    AccessMap | undefined;
  /** The accepted monthly period this order is in — locks its commission inputs. */
  closedPeriod: ClosedPeriod | null;
  source:        CustomerSource;
  customerUrlId: string;
}) {
  const id = customerUrlId;
  const { reachesTab } = useCurrentUserContext();

  // #1512 — Previous / Next through the Orders-tab list this order was opened
  // from. The list's filters arrive in the URL's search string (see
  // `order-list-search.ts`), and every link off this page carries it on, so a
  // walk of any length stays inside the filtered list and the back link
  // returns to it as it was.
  const location = useLocation();
  const navigate = useNavigate();
  const listFilters = useMemo(
    () => orderFiltersFromSearch(new URLSearchParams(location.search)),
    [location.search],
  );
  // The LIST's grant, as on the server: the buttons reveal which orders are in
  // it, so a caller who cannot open the list does not get them.
  const canWalkList = reachesTab('customers.detail.orders');
  const listSort = useMemo(
    () => orderSortFromSearch(new URLSearchParams(location.search)),
    [location.search],
  );
  const neighbours = useOrderNeighbours({
    customerId: id, orderId: order.order_id, source, colFilters: listFilters, sort: listSort,
  });
  // Stay on the tab the operator is reading — Commissions stays Commissions.
  const orderBase = orderDetailPath(source, id, order.order_id);
  const tabSuffix = location.pathname.startsWith(orderBase)
    ? location.pathname.slice(orderBase.length)
    : '';
  const goToOrder = (target: number) =>
    navigate(`${orderDetailPath(source, id, target)}${tabSuffix}${location.search}`);

  // The back link names the customer whose LIST this is — the URL's customer —
  // not the order's owner. The two differ on every frontline-retail order.
  // Same query the customer page runs, so arriving from the list it is cached.
  // Until it lands (or for a caller it is refused to) the id stands in.
  const listOwner = useCustomer(id, { source });
  const listOwnerName = [listOwner.data?.first_name, listOwner.data?.last_name]
    .filter((p) => (p ?? '').trim().length > 0).join(' ');

  // Order edit (028 Tier-2) — the Edit button lives here on the detail page; the
  // orders list links the id straight here; fetches the editable projection
  // only when the modal opens.
  //
  // Recipe step 4 — the Edit affordance DERIVES from the response's `_access`
  // map (Q21), never from a role check. `applyAccess` reads an ABSENT entry as
  // "no restriction", so a field the caller may write simply has no entry:
  // the button shows when any configured field survives that pass.
  const canEdit = useMemo(
    () => EDITABLE_ORDER_FIELDS.some(
      (name) => order._access?.[name]?.editable !== false),
    [order._access],
  );
  const [editOpen, setEditOpen] = useState(false);
  const lookups = useOrderLookups({ source });
  const editQuery = useOrderEdit(order.customer_id, editOpen ? order.order_id : null);

  // Order-LINE edit — Qty / PPV Each / CV Each. Derives from `line_access`
  // (the `order_details` section's answer) the same way Edit order derives
  // from `_access`. The modal loads its own version+lines snapshot on open,
  // because the version is the lock and `orders_v1` does not carry it.
  const editableLines = useMemo(() => editableLineFields(lineAccess), [lineAccess]);
  const canEditLines = editableLines.length > 0 && lineItems.length > 0;
  // Once the order's monthly period locks it, the WHOLE order is frozen: both
  // buttons stay, disabled, and their hover text says why. The PATCH routes
  // refuse the same edits with a 422.
  const lockedReason = closedPeriod == null
    ? undefined
    : `${closedPeriod.period_description} has been accepted — this order can no longer be edited`;
  const [linesOpen, setLinesOpen] = useState(false);
  const linesQuery = useOrderLinesEdit(order.customer_id, linesOpen ? order.order_id : null);
  const editConfig: FieldGroupConfig = useMemo(() => ({
    title:   'Edit order',
    columns: 2,
    fields: [
      { name: 'order_date', label: 'Order date', type: 'date', required: true },
      {
        name: 'order_status_id', label: 'Status', type: 'select', numeric: true,
        options: (lookups.data?.statuses ?? []).map((s) => ({ value: String(s.id), label: s.name })),
      },
      // No CV / PPV: the order's volume totals are the sum of its lines, and
      // change only through Edit lines (EditOrderLinesModal), which re-sums
      // them. Setting a total here would leave it disagreeing with its lines.
      { name: 'other15', label: orderFieldLabel('other15'), type: 'text' },
      { name: 'other12', label: orderFieldLabel('other12'), type: 'text' },
      { name: 'notes',   label: 'Notes',               type: 'text' },
      { name: 'customer_id', label: 'Reassign to customer ID', type: 'text', numeric: true, required: true },
      // #1620 — takes the order in or out of the next run.
      {
        name: 'is_commissionable', label: 'Is Commissionable', type: 'select', boolean: true, required: true,
        options: [{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }],
      },
    ],
  }), [lookups.data]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to={`${customerOrdersPath(source, id)}${location.search}`} className="text-sm text-brand-600 hover:underline">
            ← Orders for {listOwnerName || `customer ${id}`}
          </Link>
          <h1 className="mt-1 font-display text-3xl font-bold text-gray-900">
            Order {order.order_id}
            {order.order_status && (
              <Badge tone="info" className="ml-3 align-middle">{order.order_status}</Badge>
            )}
          </h1>
          <div className="mt-1 text-sm text-gray-500">
            <FormatDate date={order.order_date} format="datetime" />
            {' · '}
            {order.order_type ?? 'Unknown type'}
            {' · '}
            <span className="font-mono">customer {order.customer_id}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {(canEdit || canEditLines) && (
            <div className="flex gap-2">
              {canEditLines && (
                <Button
                  variant="secondary"
                  disabled={lockedReason != null}
                  title={lockedReason}
                  onClick={() => setLinesOpen(true)}
                >
                  Edit lines
                </Button>
              )}
              {canEdit && (
                <Button
                  variant="secondary"
                  disabled={lockedReason != null}
                  title={lockedReason}
                  onClick={() => setEditOpen(true)}
                >
                  Edit order
                </Button>
              )}
            </div>
          )}
          {/* #1512 — below the edit buttons, and NOT inside their condition:
              a caller who cannot edit still reads orders one after another.
              Previous is the row ABOVE in the list (newer — it is order_id
              DESC), Next the row below. Disabled at either end, and until the
              answer arrives rather than guessing. */}
          {canWalkList && (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                disabled={neighbours.data?.prev == null}
                onClick={() => neighbours.data?.prev != null && goToOrder(neighbours.data.prev)}
              >
                ← Previous order
              </Button>
              <Button
                variant="secondary"
                disabled={neighbours.data?.next == null}
                onClick={() => neighbours.data?.next != null && goToOrder(neighbours.data.next)}
              >
                Next order →
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Tabs">
          {TABS.filter((t) => reachesTab(t.reachTab)).map((t) => (
            <NavLink
              key={t.to}
              // The list's filters ride along, so switching tab does not drop
              // them and Previous / Next keep walking the same list (#1512).
              to={{ pathname: t.to, search: location.search }}
              end={t.end}
              className={({ isActive }) =>
                clsx(
                  'whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-brand-500 text-brand-700'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                )
              }
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <Outlet context={{ order, lineItems, source } satisfies OrderOutletCtx} />

      {editOpen && editQuery.data && (
        <EditFieldGroupModal
          open
          onClose={() => setEditOpen(false)}
          entity={editQuery.data}
          endpoint={`/api/customers/${order.customer_id}/orders/${order.order_id}`}
          invalidateKeys={[
            ['order', source, 'detail'],
            ['order', 'edit', order.customer_id, order.order_id],
            // An edit can move this order into or out of the filtered list,
            // which changes its neighbours' neighbours (#1512).
            [...ORDER_NEIGHBOURS_KEY(source)],
          ]}
          // Fold `_access` into the fields, like every other EditFieldGroupModal
          // caller: order_date/status/notes/other12/other15 are the `orders` unit and
          // CV/PPV/customer_id are `order_volumes`, so a caller holding one
          // sees the other read-only and excluded from the patch, not a field
          // the PATCH refuses.
          config={{ ...editConfig, fields: applyAccess(editConfig.fields, order._access) }}
        />
      )}

      {linesOpen && linesQuery.data && (
        <EditOrderLinesModal
          open
          onClose={() => setLinesOpen(false)}
          customerId={order.customer_id}
          orderId={order.order_id}
          dxVersion={linesQuery.data.dx_version}
          lines={linesQuery.data.line_items}
          editable={editableLines}
          invalidateKeys={[
            ['order', source, 'detail'],
            ['order', source, 'timeline'],
            ['order', 'edit', order.customer_id, order.order_id],
            ['order', 'lines-edit', order.customer_id, order.order_id],
          ]}
        />
      )}
    </div>
  );
}


// --- Route-mounted tab wrappers ------------------------------------------
// Each reads the order from the layout's Outlet context, so the tabs are
// real URL segments (see TABS above) rather than local state.

export function OrderInvoiceTab() {
  const { order, lineItems, source } = useOrderOutletCtx();
  return <InvoiceTab order={order} lineItems={lineItems} source={source} />;
}

export function OrderCommissionsTab() {
  const { order, source } = useOrderOutletCtx();
  return <CommissionsTab order={order} source={source} />;
}

export function OrderTimelineTab() {
  const { order, source } = useOrderOutletCtx();
  return <TimelineTab orderId={order.order_id} source={source} />;
}

// --- Invoice tab -----------------------------------------------------------

/**
 * Blocks 1–5 + the bottom Invoice/Line Items summary (#859).
 *
 * Layout and components are shared with Customer Summary (`./section`) so the
 * two detail pages read as one system. Money renders to 2 decimals in the
 * order's own currency (orders can be EUR); Qty renders as a whole number.
 *
 * Fields the spec asks for that do not exist in `app.orders_v1` — Sales Tax ID
 * and an order-level Reference1 (it is line-level only) — are omitted rather
 * than guessed at, pending the questions on #859.
 */
function InvoiceTab({ order, lineItems, source }: {
  order:     OrderHeader;
  lineItems: OrderLine[];
  source:    CustomerSource;
}) {
  const ccy = order.currency_code;
  // Lines arrive in order_line order, which is not displayed — so no heading
  // starts out sorted.
  const lines = useSortedRows(lineItems, {
    item_code:   (li) => li.item_code,
    description: (li) => li.item_description,
    qty:         (li) => Number(li.quantity),
    price_each:  (li) => Number(li.price_each),
    price_ext:   (li) => Number(li.price_total),
    ppv_each:    (li) => Number(li.business_volume_each),
    ppv:         (li) => Number(li.business_volume),
    cv_each:     (li) => Number(li.commissionable_volume_each),
    cv:          (li) => Number(li.commissionable_volume),
  });
  const lh = { sort: lines.sort, onToggle: lines.toggle };
  return (
    <Card>
      {/* Masked-field rendering for every block below — all of them read the
          ORDER entity, so one provider covers the page. */}
      <SectionAccess access={order._access}>
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
          {/* BLOCK 1 — Ship To */}
          <Section title="Ship to" bare>
            <div className="space-y-0.5 text-sm text-gray-900">
              {order.company && <div>{order.company}</div>}
              <div>{joinName(order)}</div>
              <div>{order.address1}</div>
              {order.address2 && <div>{order.address2}</div>}
              {order.address3 && <div>{order.address3}</div>}
              <div>{[order.city, order.state, order.zip].filter(Boolean).join(', ')}</div>
              <div>{order.country}</div>
              {order.email && <div className="pt-1 text-gray-500">{order.email}</div>}
              {order.phone && <div className="text-gray-500">{order.phone}</div>}
            </div>
          </Section>

          {/* BLOCK 2 — Order Info */}
          <Section title="Order info">
            {/* The legacy "Order Number" (other19, the Exigo order-number string) was
                removed per #996 — Order ID below is the system identifier. */}
            <Field label="Order ID" field="order_id"><span className="font-mono">{order.order_id}</span></Field>
            <Field label="Order Type" field="order_type_id">{order.order_type ?? `id ${order.order_type_id}`}</Field>
            <Field label="Order Status" field="order_status_id">{order.order_status ?? `id ${order.order_status_id}`}</Field>
            <Field label="Customer ID" field="customer_id">
              <Link to={customerDetailPath(source, order.customer_id)} className="font-mono text-brand-600 hover:underline">
                {order.customer_id}
              </Link>
            </Field>
            <Field label="Customer Name">{joinName(order)}</Field>
            <Field label="Currency Code" field="currency_code">{ccy}</Field>
          </Section>
        </div>

        {/* BLOCK 3 — Commission. Two real columns reading VERTICALLY (down the
            left, then down the right), following the #915 convention: two
            sibling <dl>s rather than one 4-track grid, both `content-start` so
            the shorter side's rows don't stretch to the taller column's height. */}
        <Section title="Commission" bare>
          <div className="grid grid-cols-1 gap-x-8 lg:grid-cols-2">
            <dl className="grid content-start grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[160px_1fr]">
              <Field label="PPV" field="business_volume_total">
                <span className="font-mono tabular-nums">{decimal2(order.business_volume_total)}</span>
              </Field>
              <Field label="CV" field="commissionable_volume_total">
                <span className="font-mono tabular-nums">{decimal2(order.commissionable_volume_total)}</span>
              </Field>
              <Field label="Is Commissionable" field="is_commissionable">{order.is_commissionable ? 'True' : 'False'}</Field>
              {/* app.orders.commissioned_date, projected by app.orders_v1: the
                  moment the acceptance pipeline stamped the order as commissioned by
                  an accepted run. Read-only — no edit surface writes it. NULL, so
                  "—", for an order no accepted run has commissioned. */}
              <Field label="Commissioned Date" field="commissioned_date">
                {commissionedDate(order)
                  ? <FormatDate date={commissionedDate(order)!} format="datetime" />
                  : null}
              </Field>
              {ORDER_SLOTS_LEFT.map((slot) => (
                <OrderSlotField key={slot} order={order} slot={slot} />
              ))}
            </dl>
            <dl className="grid content-start grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[160px_1fr]">
              {ORDER_SLOTS_RIGHT.map((slot) => (
                <OrderSlotField key={slot} order={order} slot={slot} />
              ))}
            </dl>
          </div>
          {/* isLead (Exigo LeadPoolCustomer / field13) is a customer-level-only
              attribute — intentionally removed from the order level in Exigo (~Feb 2025)
              and editable on the customer summary tab's Compliance section. It must not
              appear as an order-level field (issue #814). */}
        </Section>

        {/* BLOCK 4 — Modification Info. Two vertical-reading columns, same
            convention as Block 3. Dates and their actors stay together on the
            left; the id/reference fields group on the right. */}
        <Section title="Modification info" bare>
          <div className="grid grid-cols-1 gap-x-8 lg:grid-cols-2">
            <dl className="grid content-start grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[160px_1fr]">
              <Field label="Order Date" field="order_date"><FormatDate date={order.order_date} format="datetime" /></Field>
              <Field label="Created Date" field="created_date"><FormatDate date={order.created_date} format="datetime" /></Field>
              <Field label="Created By" field="created_by"><span className="font-mono">{order.created_by}</span></Field>
              <Field label="Modified Date" field="modified_date"><FormatDate date={order.modified_date} format="datetime" /></Field>
              <Field label="Modified By" field="modified_by"><span className="font-mono">{order.modified_by}</span></Field>
            </dl>
            <dl className="grid content-start grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[160px_1fr]">
              <Field label="Return Order ID" field="return_order_id">
                {order.return_order_id ? <span className="font-mono">{order.return_order_id}</span> : null}
              </Field>
              <Field label="Replacement Order ID" field="replacement_order_id">
                {order.replacement_order_id ? <span className="font-mono">{order.replacement_order_id}</span> : null}
              </Field>
              <Field label="Notes" field="notes"><span className="whitespace-pre-wrap">{order.notes}</span></Field>
              {/* Not in the #859 block spec, but present on the page before this rebuild —
                  kept rather than silently dropped. Shipped date is retained for the same
                  reason despite #815 (nothing has written it since 2025-09-03). */}
              {hasTracking(order) && (
                <Field label="Tracking">
                  <span className="break-all font-mono">{trackingList(order)}</span>
                </Field>
              )}
              <Field label="Shipped Date" field="shipped_date">
                {order.shipped_date ? <FormatDate date={order.shipped_date} format="datetime" /> : null}
              </Field>
            </dl>
          </div>
        </Section>

        {/* BLOCK 5 — Order Detail by Product */}
        <Section title="Order detail by product" bare>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <SortableTh {...lh} col="item_code" className="w-40">Item Code</SortableTh>
                <SortableTh {...lh} col="description">Description</SortableTh>
                <SortableTh {...lh} col="qty" className="w-16 text-right">Qty</SortableTh>
                <SortableTh {...lh} col="price_each" className="w-24 text-right">Price Each</SortableTh>
                <SortableTh {...lh} col="price_ext" className="w-24 text-right">Price Ext</SortableTh>
                <SortableTh {...lh} col="ppv_each" className="w-20 text-right">PPV Each</SortableTh>
                <SortableTh {...lh} col="ppv" className="w-20 text-right">PPV</SortableTh>
                <SortableTh {...lh} col="cv_each" className="w-20 text-right">CV Each</SortableTh>
                <SortableTh {...lh} col="cv" className="w-20 text-right">CV</SortableTh>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(lines.rows ?? []).map((li) => (
                <tr key={li.order_line}>
                  <Td className="font-mono">{li.item_code}</Td>
                  <Td>{li.item_description}</Td>
                  <Td className="text-right tabular-nums">{qty(li.quantity)}</Td>
                  <Td className="text-right tabular-nums"><FormatMoney cents={toCents(li.price_each)} currency={ccy} /></Td>
                  <Td className="text-right tabular-nums"><FormatMoney cents={toCents(li.price_total)} currency={ccy} /></Td>
                  <Td className="text-right tabular-nums">{decimal2(li.business_volume_each)}</Td>
                  <Td className="text-right tabular-nums">{decimal2(li.business_volume)}</Td>
                  <Td className="text-right tabular-nums">{decimal2(li.commissionable_volume_each)}</Td>
                  <Td className="text-right tabular-nums">{decimal2(li.commissionable_volume)}</Td>
                </tr>
              ))}
              {lineItems.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-6 text-center text-sm italic text-gray-400">
                    No line items
                  </td>
                </tr>
              )}
            </tbody>
            {/* #859: the invoice totals block. Money to 2 decimals, PPV/CV to 2
                (the source carries 4). Every figure is an order-level column —
                nothing is summed client-side, so these agree with the source
                even when a line item is missing or a discount sits off-line.
                "Amount Paid" is deliberately absent: no backing column exists on
                app.orders_v1 (flagged on the issue, 2026-07-21) and inventing it
                by summing payments would be a guess on a money figure. */}
            {lineItems.length > 0 && (
              <tfoot className="border-t-2 border-gray-200">
                <TotalRow label="Subtotal"><FormatMoney cents={toCents(order.sub_total)} currency={ccy} /></TotalRow>
                <TotalRow label="Shipping"><FormatMoney cents={toCents(order.shipping_total)} currency={ccy} /></TotalRow>
                <TotalRow label="Taxes"><FormatMoney cents={toCents(order.tax_total)} currency={ccy} /></TotalRow>
                <TotalRow label="Total" strong><FormatMoney cents={toCents(order.total)} currency={ccy} /></TotalRow>
                <TotalRow label="PPV">{decimal2(order.business_volume_total)}</TotalRow>
                <TotalRow label="CV">{decimal2(order.commissionable_volume_total)}</TotalRow>
              </tfoot>
            )}
          </table>
        </Section>
      </div>
      </SectionAccess>
    </Card>
  );
}

/**
 * One Exigo order custom-field slot. other16 (ShopifyOrderID) is the only slot
 * that links: it holds the numeric id the Shopify admin URL takes. other19 is
 * the human order *number* (e.g. "PLO1100096") and would produce a dead link.
 */
function OrderSlotField({ order, slot }: { order: OrderHeader; slot: string }) {
  const value = order[slot] as string | null;
  return (
    <Field label={orderFieldLabel(slot)} field={slot}>
      {slot === 'other16'
        ? <ShopifyOrderLink id={value} shopDomain={order.other20} className="font-mono" />
        : <span className="font-mono">{nullable(value)}</span>}
    </Field>
  );
}

// Th / Td / Muted now live in ./order-payouts-table, which the Commissions tab
// shares with the run-scoped order page (#1213); re-exported through the import
// above so the other tabs here keep using them unchanged.

/**
 * One line of the invoice totals block (#859). The label sits in the last
 * column before the value so the figures stay right-aligned under the line
 * items' own money columns, matching the Exigo invoice the issue reproduces.
 */
function TotalRow({ label, children, strong }: { label: string; children: ReactNode; strong?: boolean }) {
  return (
    <tr>
      <td colSpan={7} />
      <td className={clsx('px-3 py-1 text-right text-sm', strong ? 'font-semibold text-gray-900' : 'text-gray-600')}>
        {label}
      </td>
      <td className={clsx('px-3 py-1 text-right text-sm tabular-nums', strong ? 'font-semibold text-gray-900' : 'text-gray-900')}>
        {children}
      </td>
    </tr>
  );
}

// --- Commissions tab -------------------------------------------------------

/**
 * "Who got paid commissions on this order" (#859), in ONE snapshot.
 *
 * Leigh's complaint about Exigo was having to page through the enroller tree
 * one bonus level at a time (PB1..PB5) to assemble this picture, so every
 * accepted run and every bonus is listed together here, with the per-bonus
 * tree drill-down kept as an optional link out to the existing run-scoped
 * analysis pages rather than rebuilt.
 *
 * Rows are grouped by run because an order can be paid by more than one
 * accepted run (~76.7k orders locally) — collapsing them would hide real
 * payouts. Empty is a legitimate state: an order in the current open period
 * has no accepted run yet, which is expected and not a bug.
 */
function CommissionsTab({ order, source }: { order: OrderHeader; source: CustomerSource }) {
  const q = useOrderCommissions(order.order_id, { source });
  const rows = q.data?.rows ?? [];

  // Preserve server ordering (accepted_date DESC) while grouping by run.
  const runs = useMemo(() => {
    const byRun = new Map<number, OrderCommissionRow[]>();
    for (const r of rows) {
      const list = byRun.get(r.commission_run_id);
      if (list) list.push(r);
      else byRun.set(r.commission_run_id, [r]);
    }
    // Within each run, pair each payout with its matching deduction (#997).
    // Array.prototype.sort is stable, so rows sharing a bonus type keep the
    // server's accepted_date DESC order.
    for (const list of byRun.values()) {
      list.sort((a, b) => bonusTypeRank(a.bonus_type) - bonusTypeRank(b.bonus_type));
    }
    return [...byRun.entries()];
  }, [rows]);

  if (q.isLoading) {
    return <Card><div className="flex justify-center py-12"><LoadingSpinner /></div></Card>;
  }

  // 403 is meaningful here: `commissions:read` is held independently of the
  // `customers:read` that got the operator to this page.
  if (q.error) {
    return (
      <Card>
        <EmptyState
          title={q.error.status === 403 ? 'Not permitted' : "Couldn't load commissions"}
          description={q.error.status === 403
            ? 'Viewing commission payouts requires the commissions:read privilege.'
            : q.error.message}
        />
      </Card>
    );
  }

  if (runs.length === 0) {
    return (
      <Card>
        <EmptyState
          title="No commissions paid on this order"
          description="No accepted commission run has paid on this order. Orders in the current open period show nothing here until that period is accepted."
        />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {runs.map(([runId, payouts]) => (
        <Card key={runId}>
          <OrderPayoutsTable
            sortable
            rows={payouts}
            source={source}
            headerSuffix={
              <Link
                to={`/commissions/${runId}/order/${order.order_id}`}
                className="ml-2 font-mono text-xs font-normal text-brand-600 hover:underline"
              >
                run {runId}
              </Link>
            }
          />
        </Card>
      ))}
    </div>
  );
}

// --- Timeline tab ----------------------------------------------------------

function TimelineTab({ orderId, source }: { orderId: number; source: CustomerSource }) {
  const timeline = useOrderTimeline(orderId, { source });
  const lookups  = useOrderLookups({ source });
  const events = timeline.data?.rows ?? [];

  return (
    <Card>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Change Log</h2>
        <p className="text-sm text-gray-500">
          Admin edits, plus events mirrored from{' '}
          <code className="font-mono text-xs">OrderChangeLog</code> +{' '}
          <code className="font-mono text-xs">OrderStatusChangeLog</code>. Newest first; limit 200.
        </p>
      </div>

      {timeline.isLoading || lookups.isLoading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : timeline.error ? (
        <EmptyState title="Couldn't load timeline" description={timeline.error.message} />
      ) : events.length === 0 ? (
        <EmptyState
          title="No timeline events"
          description="No admin edits, and no change-log entries mirrored for this order yet."
        />
      ) : (
        <ol className="relative mt-2 space-y-4 border-l-2 border-gray-100 pl-4">
          {events.map((e) => (
            <li key={`${e.event_type}-${e.event_id}`} className="relative">
              <span
                className={`absolute -left-[21px] top-1 h-3 w-3 rounded-full border-2 border-white ${dotClass(e.event_type)}`}
                aria-hidden="true"
              />
              <EventRow event={e} lookups={lookups.data} />
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function EventRow({ event, lookups }: { event: OrderTimelineEvent; lookups: OrderLookups | undefined }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        {event.event_date && <FormatDate date={event.event_date} format="datetime" />}
        <Badge tone={typeTone(event.event_type)}>{event.event_type}</Badge>
        {event.actor_label ? (
          <span className="text-gray-700" title={event.actor}>by {event.actor_label}</span>
        ) : (
          <span className="font-mono text-[10px] text-gray-400" title="modified_by (no Users match)">
            {event.actor.slice(0, 16)}
          </span>
        )}
      </div>
      <div className="text-sm text-gray-900 whitespace-pre-wrap">{describe(event, lookups)}</div>
    </div>
  );
}

function describe(event: OrderTimelineEvent, lookups: OrderLookups | undefined): string {
  if (event.event_type === 'change') return event.payload ?? '(no detail)';
  // #1238 — an admin edit: an app.order_change_log row carrying a dx_reason.
  // The view appends " [reason: …]" to its detail, so the operator's reason
  // shows here. Must precede the status fallback: these carry no to_id, and would
  // otherwise render as "Status changed (id ?)".
  if (event.event_type === 'edit') return event.payload ?? 'Order edited';
  // status
  const name = lookups?.statuses.find((s) => s.id === event.to_id)?.name;
  return name ? `Status changed to ${name}` : `Status changed (id ${event.to_id ?? '?'})`;
}

function typeTone(t: OrderTimelineEvent['event_type']): 'info' | 'neutral' {
  return t === 'status' ? 'info' : 'neutral';
}

function dotClass(t: OrderTimelineEvent['event_type']): string {
  if (t === 'status') return 'bg-blue-500';
  if (t === 'edit')   return 'bg-indigo-500';
  return 'bg-gray-400';
}

// --- helpers ---------------------------------------------------------------

function toCents(numericString: string): number {
  const n = Number(numericString);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

function nullable(v: string | null): React.ReactNode {
  return v && v.trim().length > 0 ? v : <Muted>—</Muted>;
}

// decimal2 moved to ./order-payouts-table alongside the table primitives (#1213).

/**
 * `commissioned_date` as a non-empty string, or null for an order no accepted
 * run has commissioned.
 */
function commissionedDate(order: OrderHeader): string | null {
  const v = order.commissioned_date;
  return typeof v === 'string' && v.length > 0 ? v : null;
}

// netByCurrency / netUsd moved to ./order-payouts-table (#1213).

/** Quantity as a whole number — the source carries "1.00" (#859). */
function qty(v: string | null | undefined): string {
  const n = Number(v);
  return Number.isFinite(n) ? String(Math.round(n)) : '—';
}

function joinName(o: OrderHeader): string {
  return [o.first_name, o.middle_name, o.last_name, o.name_suffix].filter(Boolean).join(' ');
}

function hasTracking(o: OrderHeader): boolean {
  return [o.tracking_number1, o.tracking_number2, o.tracking_number3, o.tracking_number4, o.tracking_number5]
    .some((t) => !!t && t.trim().length > 0);
}

function trackingList(o: OrderHeader): string {
  return [o.tracking_number1, o.tracking_number2, o.tracking_number3, o.tracking_number4, o.tracking_number5]
    .filter((t) => !!t && t.trim().length > 0)
    .join(', ');
}
